import { useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatMoney, hoy } from '@abastece/shared';
import { db } from '../db/dexie.js';
import { rangoDe } from '../lib/historial.js';
import {
  RESULTADOS_TURNO,
  SIN_FILTROS_TURNOS,
  filtrosDeTurnos,
  ordenarTurnos,
  resumenTurnos,
  turnoCoincide,
  type FiltroTurnos,
  type ResultadoTurno,
} from '../lib/historialDinero.js';
import { TarjetaTurno } from '../ui/FilasDeDinero.js';
import { CorregirConteo } from '../ui/CorregirConteo.js';
import { usePuede } from '../lib/sesion.js';
import { BuscarEnLista, Desplegable, FilaDesplegables, FiltroFechas, FiltrosEnLinea } from '../ui/FiltrosHistorial.js';
import { Ico } from '../ui/Iconos.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { useT } from '../i18n/index.js';

/** How many show at a time: the latest, and «Ver más» for the rest. */
const LOTE = 50;

/**
 * «Turnos anteriores», all of them: every shift gone by, found by the day it opened, by whoever had
 * it, and by what its counts found. «Caja» keeps the latest ten while no till is open.
 */
export function Turnos() {
  const t = useT();
  const hoyEs = hoy();
  const sesiones = useLiveQuery(() => db.sesiones.where('estado').equals('cerrada').toArray(), [], undefined);
  const usuarios = useLiveQuery(() => db.usuarios.toArray(), [], []);
  const [filtro, setFiltro] = useState<FiltroTurnos>(SIN_FILTROS_TURNOS);
  const [cuantos, setCuantos] = useState(LOTE);
  // A count put right: what was counted is money already counted, the owner's (`configurar`).
  const puedeCorregir = usePuede('configurar');
  const [corrigiendo, setCorrigiendo] = useState<{ sesionId: string; cual: 'apertura' | 'corte' } | null>(null);

  const rango = rangoDe(filtro.periodo, hoyEs, filtro);
  const puestos = filtrosDeTurnos(filtro, rango);
  const filtrando = puestos.length > 0;
  const cambiar = (cambio: Partial<FiltroTurnos>) => {
    setFiltro((f) => ({ ...f, ...cambio }));
    setCuantos(LOTE);
  };
  const encontrados = useMemo(
    () => ordenarTurnos((sesiones ?? []).filter((s) => turnoCoincide(s, filtro, rango))),
    // `rango` is worked out afresh each time: its two days are what it is.
    [sesiones, filtro, rango.desde, rango.hasta],
  );
  const resumen = resumenTurnos(encontrados);
  const nombre = (id: string) => usuarios.find((u) => u.id === id)?.nombre ?? t('ventas.alguien');
  const cuantosHay = sesiones?.length ?? 0;

  return (
    <Pagina titulo={t('caja.turnosAnteriores')} subtitulo={t('turnosHist.registrados', { n: cuantosHay })} volverA="/caja">
      {(filtrando || cuantosHay > 0) && (
        <FiltrosEnLinea hayPuestos={filtrando} onQuitarTodos={() => cambiar(SIN_FILTROS_TURNOS)}>
          <FiltroFechas
            periodo={filtro.periodo}
            desde={filtro.desde}
            hasta={filtro.hasta}
            ayuda={t('turnosHist.porApertura')}
            onCambio={cambiar}
          />
          <BuscarEnLista
            clave="quien"
            etiqueta={t('turnosHist.deQuien')}
            placeholder={t('turnosHist.buscarQuien')}
            opciones={usuarios}
            value={filtro.quien}
            onChange={(quien) => cambiar({ quien })}
          />
          <FilaDesplegables>
            <Desplegable
              clave="resultado"
              etiqueta={t('turnosHist.resultado')}
              value={filtro.resultado}
              onChange={(v) => cambiar({ resultado: v as ResultadoTurno })}
              elegido={filtro.resultado === 'todos' ? undefined : t(`turnosHist.resultado.${filtro.resultado}`)}
            >
              {RESULTADOS_TURNO.map((r) => (
                <option key={r} value={r}>
                  {t(`turnosHist.resultado.${r}`)}
                </option>
              ))}
            </Desplegable>
          </FilaDesplegables>
        </FiltrosEnLinea>
      )}

      {filtrando && encontrados.length > 0 && (
        <p className="tabular mb-3 text-sm text-slate-400" data-historial-resumen>
          {resumen.diferencia === 0
            ? t('turnosHist.resumen', { n: resumen.n, vendido: formatMoney(resumen.vendido) })
            : t('turnosHist.resumenDiferencia', {
                n: resumen.n,
                vendido: formatMoney(resumen.vendido),
                diferencia: formatMoney(resumen.diferencia, { signed: true }),
              })}
        </p>
      )}

      {/* Nothing said while it is read: «nothing matches» was only «not read yet». */}
      {sesiones === undefined ? null : encontrados.length === 0 ? (
        filtrando ? (
          <Vacio mensaje={t('turnosHist.ninguno')} icono="buscar">
            <button type="button" className="btn-secundario" onClick={() => cambiar(SIN_FILTROS_TURNOS)}>
              <Ico icono="reiniciar" />
              {t('historial.quitarTodos')}
            </button>
          </Vacio>
        ) : (
          <Vacio mensaje={t('caja.sinTurnos')} icono="caja" />
        )
      ) : (
        <ul className="flex flex-col gap-2" data-historial-lista>
          {encontrados.slice(0, cuantos).map((s) => (
            <TarjetaTurno
              key={s.id}
              sesion={s}
              nombre={nombre}
              {...(puedeCorregir ? { onCorregir: (cual: 'apertura' | 'corte') => setCorrigiendo({ sesionId: s.id, cual }) } : {})}
            />
          ))}
        </ul>
      )}
      {encontrados.length > cuantos && (
        <button type="button" className="btn-secundario mt-4 w-full" onClick={() => setCuantos((n) => n + LOTE)}>
          <Ico icono="desplegar" />
          {t('comun.verMas')}
        </button>
      )}
      {corrigiendo && <CorregirConteo {...corrigiendo} onCerrar={() => setCorrigiendo(null)} />}
    </Pagina>
  );
}
